import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Fragment
} from 'react'
import Select, { components, SingleValue } from 'react-select'
import axios from 'axios'
import OrderModal from './OrderModal'
import OrdersTable from './orders_table/OrdersTable'
import { OrdersTab } from './orders_table/columns'
import RefreshControl from './RefreshControl'
import IssuesTable from './orders_table/IssuesTable'
import Tabs from '../shared/Tabs'
import { t } from '../../i18n'
import Icon from '../shared/icon'
import {
  LocalizationContext,
  DEFAULT_LOCALIZATION_CONTEXT,
  LocalizationContextType
} from '../shared/contexts'
import { usePolling, PollFetcher } from '../../hooks/usePolling'
import {
  TableFilters,
  FilterField,
  FilterKind,
  isRangeKind
} from '../shared/table_filters'
import {
  OrderGroup,
  OrderRow,
  OrderGroupsResponse,
  OrderIssueRow,
  OrderIssuesResponse,
  SelectOption,
  OrderProvider
} from './types'

const scope = 'orders'

const POLL_INTERVAL_MS = 60000

const filterField = (id: string, kind: FilterKind = 'text'): FilterField => {
  const field: FilterField = { id, kind, label: t(`filters.${id}`, { scope }) }
  if (isRangeKind(kind)) {
    field.fromParam = `${id}_from`
    field.toParam = `${id}_to`
  }
  return field
}

const statusFilterValues = [
  'received',
  'written_to_robot',
  'cooking',
  'processing',
  'ready_for_pickup',
  'picked_up',
  'marked_for_disposal',
  'disposed',
  'canceled',
  'failed'
]

const statusFilterField: FilterField = {
  id: 'status',
  kind: 'select',
  label: t('filters.status', { scope }),
  options: statusFilterValues.map((value) => ({
    value,
    label: t(`meal_statuses.${value}`, { scope })
  }))
}

const orderFilterFields: FilterField[] = [
  ...[
    'order_id',
    'queued_order_id',
    'external_id',
    'recipe_name',
    'phone_number'
  ].map((name) => filterField(name)),
  statusFilterField,
  filterField('received', 'dateTimeRange')
]

const tabs: { name: OrdersTab; label: string; heading: string }[] = [
  {
    name: 'order_queue',
    label: t('order_status_tab', { scope }),
    heading: t('order_queue', { scope })
  },
  {
    name: 'order_issues',
    label: t('order_issues', { scope }),
    heading: t('order_issues', { scope })
  },
  {
    name: 'order_history',
    label: t('order_history', { scope }),
    heading: t('order_history', { scope })
  }
]

const typeOptions = [
  { label: t('type.both', { scope }), value: '' },
  { label: t('type.meal', { scope }), value: 'meal' },
  { label: t('type.technical', { scope }), value: 'technical' }
]

const clampTabIndex = (value: number) =>
  Math.min(Math.max(Number.isNaN(value) ? 0 : value, 0), tabs.length - 1)

interface OrdersProps {
  rob_in_reset_state: boolean
  order_scheduling_enabled: boolean
  should_reset_queued_orders: boolean
  should_cancel_queued_orders: boolean
  order_queue_open: boolean
  policies: { create_order: boolean }
  currency: string
  localization_context: LocalizationContextType | null
  order_providers: OrderProvider[]
}

const Orders = (props: OrdersProps) => {
  const {
    rob_in_reset_state,
    order_scheduling_enabled,
    should_reset_queued_orders,
    should_cancel_queued_orders,
    order_queue_open,
    policies,
    currency,
    localization_context,
    order_providers
  } = props

  const searchParams = new URLSearchParams(window.location.search)

  const [orderGroups, setOrderGroups] = useState<OrderGroup[]>([])
  const [orderIssues, setOrderIssues] = useState<OrderIssueRow[]>([])
  const [issuesCount, setIssuesCount] = useState(0)
  const [manuallyScheduledCommands, setManuallyScheduledCommands] = useState<
    OrderRow[]
  >([])
  const [robInResetState, setRobInResetState] = useState(rob_in_reset_state)
  const [orderSchedulingEnabled, setOrderSchedulingEnabled] = useState(
    order_scheduling_enabled
  )
  const [shouldResetQueuedOrders, setShouldResetQueuedOrders] = useState(
    should_reset_queued_orders
  )
  const [shouldCancelQueuedOrders, setShouldCancelQueuedOrders] = useState(
    should_cancel_queued_orders
  )
  const [currentPage, setCurrentPage] = useState(
    searchParams.get('page') || '1'
  )
  const [type, setType] = useState(searchParams.get('type') || '')
  const [currentTabIndex, setCurrentTabIndex] = useState(
    clampTabIndex(parseInt(searchParams.get('tab') || '', 10))
  )
  const [pagesCount, setPagesCount] = useState<number | null>(null)
  const [filterParams, setFilterParams] = useState<Record<string, string>>({})

  const [isMutating, setIsMutating] = useState(false)

  const currentTabName = tabs[currentTabIndex].name

  const activeTabRef = useRef(currentTabName)
  activeTabRef.current = currentTabName

  const typeRef = useRef(type)
  typeRef.current = type

  const historyPageRef = useRef(currentPage)
  historyPageRef.current = currentPage

  const filterParamsRef = useRef(filterParams)
  filterParamsRef.current = filterParams

  const loadActiveTab = useCallback<PollFetcher>(async (signal) => {
    // The tab red-dot badge stays fresh from any tab, so fetch the 24h count every poll.
    const countRequest = axios
      .get<{ count: number }>('/orders/order_issues_count', {
        params: { format: 'json' },
        signal
      })
      .then(({ data }) => setIssuesCount(data.count))
      .catch(() => {})

    if (activeTabRef.current === 'order_issues') {
      const { data } = await axios.get<OrderIssuesResponse>(
        '/orders/order_issues',
        {
          params: {
            ...filterParamsRef.current,
            page: historyPageRef.current,
            format: 'json'
          },
          signal
        }
      )
      setOrderIssues(data.order_issues)
      setPagesCount(data.page_count)
      await countRequest
      return
    }

    const isHistoryTab = activeTabRef.current === 'order_history'
    const requestScope = isHistoryTab ? 'history' : 'queue'

    const requestParams: Record<string, string> = {
      ...filterParamsRef.current,
      type: typeRef.current,
      scope: requestScope
    }
    if (isHistoryTab) requestParams.page = historyPageRef.current

    const [orderGroupsResponse, commandsResponse] = await Promise.all([
      axios.get<OrderGroupsResponse>('/orders/order_groups', {
        params: { ...requestParams, format: 'json' },
        signal
      }),
      isHistoryTab
        ? Promise.resolve(null)
        : axios.get<OrderRow[]>('/orders/manually_scheduled_commands', {
            params: { ...requestParams, format: 'json' },
            signal
          })
    ])

    setOrderGroups(orderGroupsResponse.data.order_groups)
    setPagesCount(orderGroupsResponse.data.page_count)
    if (commandsResponse) setManuallyScheduledCommands(commandsResponse.data)
    await countRequest
  }, [])

  // Interval polling runs only on the Order Queue tab; the other tabs fetch once on
  // open and refresh only via pagination, filters, or the manual refresh button
  // (intervalMs 0 keeps the one-time fetch + refresh() but disables auto-polling).
  const pollIntervalMs =
    currentTabName === 'order_queue' ? POLL_INTERVAL_MS : 0

  const {
    refresh,
    isFetching,
    lastUpdatedAt,
    error: pollError
  } = usePolling(loadActiveTab, pollIntervalMs, !isMutating)

  const didMountRef = useRef(false)

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    refresh()
  }, [type, currentPage, currentTabIndex, filterParams, refresh])

  const resetQueue = (resetType: 'cancel' | 'reset') => {
    if (!confirm(t('global.confirm'))) return

    setIsMutating(true)
    axios
      .post('/orders/reset_queue', { type: resetType })
      .then(({ data }) => {
        if (resetType === 'cancel') {
          setShouldCancelQueuedOrders(true)
        } else {
          setShouldResetQueuedOrders(true)
        }
        alert(data)
      })
      .catch(({ response: { data } }) => alert(data))
      .finally(() => setIsMutating(false))
  }

  const handleTypeFilterChange = (typeOption: SingleValue<SelectOption>) => {
    if (!typeOption) return
    setType(typeOption.value)
    setCurrentPage('1')
  }

  const changePage = (page: number) => {
    setCurrentPage(page.toString())
  }

  const applyFilters = useCallback((params: Record<string, string>) => {
    setFilterParams(params)
    setCurrentPage('1')
  }, [])

  const switchTab = (index: number) => {
    const safeIndex = clampTabIndex(index)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', safeIndex.toString())
    window.history.pushState(null, '', url.toString())
    setFilterParams({})
    setCurrentPage('1')
    setManuallyScheduledCommands([])
    setCurrentTabIndex(safeIndex)
  }

  const renderTypeFilter = () => (
    <div className="order-type-filter">
      <Select
        className="react-select"
        classNamePrefix="react-select"
        name="typeFilter"
        value={
          typeOptions.find((option) => option.value === type) ?? typeOptions[0]
        }
        onChange={handleTypeFilterChange}
        options={typeOptions}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: (dropdownProps) => (
            <components.DropdownIndicator {...dropdownProps}>
              <Icon name="arrowDown" />
            </components.DropdownIndicator>
          )
        }}
      />
    </div>
  )

  const commandGroups: OrderGroup[] = manuallyScheduledCommands.map(
    (command) => ({
      record_type: 'order',
      order_id: null,
      order_number: null,
      phone_number: null,
      external_id: null,
      created_at_epoch: command.created_at_epoch,
      queued_orders: [command]
    })
  )

  const displayGroups =
    currentTabName === 'order_queue'
      ? [...orderGroups, ...commandGroups].sort(
          (a, b) => b.created_at_epoch - a.created_at_epoch
        )
      : orderGroups

  return (
    <LocalizationContext.Provider
      value={localization_context || DEFAULT_LOCALIZATION_CONTEXT}
    >
      <div className="row">
        <div className="orders-container col-sm-12">
          <div className="orders-header">
            <div className="section-heading ps-3 pt-2">
              <div className="section-heading-row bold">
                {tabs[currentTabIndex].heading}
              </div>
            </div>
            <Tabs
              tabs={tabs}
              currentTabIndex={currentTabIndex}
              switchTab={switchTab}
              className="tabs-wrapper"
              alerts={{ order_issues: issuesCount }}
            />
            <div className="orders-header__actions mb-3 d-flex justify-content-between align-items-center">
              {currentTabName === 'order_queue' && (
                <>
                  {!order_queue_open && (
                    <span className="text-danger me-3">
                      {t('closed_for_orders', { scope })}
                    </span>
                  )}
                  {!orderSchedulingEnabled && (
                    <span className="text-danger me-3">
                      {t('order_scheduling_disabled', { scope })}
                    </span>
                  )}
                  {robInResetState &&
                    !shouldResetQueuedOrders &&
                    !shouldCancelQueuedOrders && (
                      <Fragment>
                        <span className="me-3 text-danger fw-bold">
                          {t('robot_restarted', { scope })}
                        </span>
                        <button
                          className="btn btn-danger me-3"
                          onClick={() => resetQueue('cancel')}
                        >
                          {t('cancel_queue', { scope })}
                        </button>
                        <button
                          className="btn btn-danger me-3"
                          onClick={() => resetQueue('reset')}
                        >
                          {t('restart_queue', { scope })}
                        </button>
                      </Fragment>
                    )}
                  {policies.create_order && (
                    <OrderModal
                      newOrderAvailable={order_queue_open}
                      currency={currency}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <TableFilters
            key={currentTabName}
            fields={orderFilterFields}
            leadingControls={
              <RefreshControl
                onRefresh={refresh}
                isFetching={isFetching}
                lastUpdatedAt={lastUpdatedAt}
                hasError={pollError != null}
              />
            }
            primaryControls={renderTypeFilter()}
            onApply={applyFilters}
          />

          <div className="orders-body">
            <div className="tab-content-wrapper">
              {currentTabName === 'order_issues' ? (
                <IssuesTable
                  issues={orderIssues}
                  currentPage={currentPage}
                  pageCount={pagesCount}
                  onPageChange={(_e, page) => changePage(page)}
                />
              ) : (
                <OrdersTable
                  tab={currentTabName}
                  orderGroups={displayGroups}
                  orderProviders={order_providers}
                  currentPage={currentPage}
                  pageCount={pagesCount}
                  onPageChange={(_e, page) => changePage(page)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </LocalizationContext.Provider>
  )
}

export default Orders
